import { toast, ToggleSwitch } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import {
  Button,
  closeModal,
  Modal,
  openModal,
  TextInput,
} from "risa-oasis-ui_v2";
import { ToastMessages } from "../../../../../../components/toasts/toastMessages";
import { ModalId } from "../../../../../../enums/modalId";
import { TrashIcon } from "../../../../../../svg/trashIcon";

interface DxSourcePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Persists the edited policy to the selected facility. */
  onSave: (policy: Record<string, boolean>) => Promise<void>;
  /** Current facility `dx_source_policy`, seeded into the form on open. */
  initialPolicy: Record<string, boolean>;
  loading?: boolean;
}

/**
 * Editor for a facility's `dx_source_policy`. Lets the user add new source
 * types, toggle existing ones on/off, and remove a key. The policy's keys are
 * the sole source of the DX code "Type" dropdown, so edits here drive what
 * codes can be tagged as.
 */
const DxSourcePolicyModal: React.FC<DxSourcePolicyModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialPolicy,
  loading = false,
}) => {
  const [policy, setPolicy] = useState<Record<string, boolean>>({});
  const [newSourceType, setNewSourceType] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Open/close the native dialog and (re)seed the form from the current policy.
  useEffect(() => {
    if (isOpen) {
      setPolicy({ ...initialPolicy });
      setNewSourceType("");
      openModal(ModalId.DX_SOURCE_POLICY_MODAL);
    } else {
      closeModal(ModalId.DX_SOURCE_POLICY_MODAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleToggle = (name: string, value: boolean) => {
    setPolicy((prev) => ({ ...prev, [name]: value }));
  };

  const handleRemove = (name: string) => {
    setPolicy((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleAddSourceType = () => {
    const trimmed = newSourceType.trim();
    if (!trimmed) {
      return;
    }
    // Case-insensitive uniqueness keeps keys from colliding in the dropdown.
    const exists = Object.keys(policy).some(
      (key) => key.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      toast.error(ToastMessages.DX_SOURCE_POLICY_DUPLICATE_ERROR);
      return;
    }
    setPolicy((prev) => ({ ...prev, [trimmed]: true }));
    setNewSourceType("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(policy);
      closeModal(ModalId.DX_SOURCE_POLICY_MODAL);
      onClose();
    } catch {
      // Save failed; the parent already surfaced a toast. Keep the dialog open
      // so the user can retry their edits.
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    closeModal(ModalId.DX_SOURCE_POLICY_MODAL);
    onClose();
  };

  const canAdd = newSourceType.trim().length > 0;

  return (
    <Modal
      dialogId={ModalId.DX_SOURCE_POLICY_MODAL}
      title="DX Source Policy"
      onSave={handleSave}
      cancelText="Cancel"
      saveButtonText={isSaving ? "Saving..." : "Save"}
      disableSave={loading || isSaving}
      onClose={handleCancel}
    >
      <div className="dx-source-policy-modal flex flex-col gap-4">
        <div className="text-xs leading-normal text-primaryGray-6">
          Add source types and toggle whether each is enabled. These keys
          populate the DX code Type dropdown for this organization.
        </div>

        {/* Existing source types */}
        <div className="flex flex-col gap-1">
          {Object.keys(policy).length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500">
              No source types configured yet
            </div>
          ) : (
            Object.entries(policy).map(([name, enabled]) => (
              <div
                key={name}
                className="flex items-center justify-between border-b border-[#e5e7eb] py-2"
              >
                <span className="truncate text-sm font-normal">{name}</span>
                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    // Remount on value change so the uncontrolled switch
                    // reflects external updates (e.g. re-opening the modal).
                    key={`${name}-${enabled}`}
                    id={`dx-source-policy-${name}`}
                    defaultChecked={enabled}
                    onChangeEmit={(data: { value: boolean }) =>
                      handleToggle(name, data.value)
                    }
                  />
                  <button
                    className="flex cursor-pointer items-center justify-center border-none bg-transparent p-1 hover:opacity-70"
                    onClick={() => handleRemove(name)}
                    aria-label={`Remove ${name}`}
                  >
                    <TrashIcon
                      width={16}
                      height={16}
                      className="h-4 w-4 text-[#BB0202]"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add a new source type */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextInput
              id="new-dx-source-type"
              label="New Source Type"
              placeholder="e.g. CMS, Custom, NCCN"
              onChange={(data) => setNewSourceType(data.value)}
              defaultValue={newSourceType}
            />
          </div>
          <Button
            buttonType="secondary"
            size="medium"
            disabled={!canAdd}
            onClick={handleAddSourceType}
          >
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DxSourcePolicyModal;
